import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import api from '../api/api';
import PropertyFeed from '../components/propertyFeed';
import DirectBookingPricingRules from '../components/directBookingPricingRules';
import { useUser } from '../context/UserContext';
import 'react-datepicker/dist/react-datepicker.css';
import './propertyDetails.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BOOKING_SOURCES = ['Airbnb', 'VRBO', 'Bookings.com', 'Direct'];
const CAPABILITY_OPTIONS = [
    { key: 'property.read', label: 'Property Read' },
    { key: 'property.write', label: 'Property Write' },
    { key: 'cohost.manage', label: 'Manage Co-hosts' },
    { key: 'financials.read', label: 'Financials Read' },
    { key: 'financials.write', label: 'Financials Write' },
    { key: 'payouts.read', label: 'Payouts Read' },
    { key: 'payouts.write', label: 'Payouts Write' },
    { key: 'tasks.read', label: 'Tasks Read' },
    { key: 'tasks.write', label: 'Tasks Write' },
    { key: 'messages.read', label: 'Messages Read' },
    { key: 'messages.write', label: 'Messages Write' },
    { key: 'notes.read', label: 'Notes Read' },
    { key: 'notes.write', label: 'Notes Write' },
    { key: 'receipts.read', label: 'Receipts Read' },
    { key: 'receipts.write', label: 'Receipts Write' }
];

const CAPABILITY_GROUPS = [
    { label: 'Property', keys: ['property.read', 'property.write', 'cohost.manage'] },
    { label: 'Financials', keys: ['financials.read', 'financials.write', 'payouts.read', 'payouts.write'] },
    { label: 'Operations', keys: ['tasks.read', 'tasks.write', 'messages.read', 'messages.write'] },
    { label: 'Records', keys: ['notes.read', 'notes.write', 'receipts.read', 'receipts.write'] }
];

const STATUS_META = {
    active: { label: 'Active', color: '#15803d' },
    paused: { label: 'Paused', color: '#b45309' },
    removed: { label: 'Removed', color: '#b91c1c' }
};

const ROLE_LABELS = { owner_coowner: 'Owner / Co-owner', cleaner: 'Cleaner', view: 'View only' };

const PRESET_OPTIONS = [
    {
        key: 'operations_manager',
        label: 'Operations Manager',
        apply: () => ({
            role: 'owner_coowner',
            accessStatus: 'active',
            allow: ['tasks.write', 'messages.write', 'property.write', 'notes.write', 'receipts.write'],
            deny: ['financials.write', 'payouts.write']
        })
    },
    {
        key: 'finance_view',
        label: 'Finance View',
        apply: () => ({
            role: 'view',
            accessStatus: 'active',
            allow: ['financials.read', 'payouts.read'],
            deny: ['financials.write', 'payouts.write', 'tasks.write', 'messages.write']
        })
    },
    {
        key: 'cleaner_standard',
        label: 'Cleaner Standard',
        apply: () => ({
            role: 'cleaner',
            accessStatus: 'active',
            allow: [],
            deny: ['financials.read', 'financials.write', 'payouts.read', 'payouts.write']
        })
    }
];

const toDateOnly = (value) => {
    const normalized = String(value || '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
};

const toDateTs = (value, fallback) => {
    const normalized = toDateOnly(value);
    if (!normalized) return fallback;
    const parsed = parseLocalDate(normalized);
    return parsed ? parsed.getTime() : fallback;
};

const parseLocalDate = (value) => {
    const normalized = String(value || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
    const [year, month, day] = normalized.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatLocalDate = (value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getPayoutSplitConflicts = (rows) => {
    const normalized = (rows || []).map((row, index) => ({ ...row, _index: index }));
    const conflicts = [];

    const byBeneficiary = normalized.reduce((acc, entry) => {
        const key = Number(entry.beneficiaryUserId);
        if (!Number.isInteger(key) || key <= 0) return acc;
        const existing = acc.get(key) || [];
        existing.push(entry);
        acc.set(key, existing);
        return acc;
    }, new Map());

    for (const [beneficiaryUserId, entries] of byBeneficiary.entries()) {
        for (let i = 0; i < entries.length; i += 1) {
            for (let j = i + 1; j < entries.length; j += 1) {
                const left = entries[i];
                const right = entries[j];
                const leftStart = toDateTs(left.effectiveStartDate, Number.NEGATIVE_INFINITY);
                const leftEnd = toDateTs(left.effectiveEndDate, Number.POSITIVE_INFINITY);
                const rightStart = toDateTs(right.effectiveStartDate, Number.NEGATIVE_INFINITY);
                const rightEnd = toDateTs(right.effectiveEndDate, Number.POSITIVE_INFINITY);

                const overlap = leftStart <= rightEnd && rightStart <= leftEnd;
                if (overlap) {
                    conflicts.push({
                        type: 'overlap',
                        message: `Beneficiary ${beneficiaryUserId} has overlapping date ranges (rows ${left._index + 1} and ${right._index + 1}).`
                    });
                }
            }
        }
    }

    const byStartDate = normalized.reduce((acc, entry) => {
        const key = toDateOnly(entry.effectiveStartDate);
        if (!key) return acc;
        const existing = acc.get(key) || [];
        existing.push(entry);
        acc.set(key, existing);
        return acc;
    }, new Map());

    for (const [effectiveStartDate, entries] of byStartDate.entries()) {
        const total = entries.reduce((sum, entry) => sum + (Number(entry.splitPercent) || 0), 0);
        if (Math.abs(total - 100) > 0.001) {
            conflicts.push({
                type: 'total',
                message: `Effective start ${effectiveStartDate} totals ${total.toFixed(3)}% instead of 100%.`
            });
        }
    }

    return conflicts;
};

const defaultUtilityRows = () => Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    waterCost: '',
    gasCost: '',
    energyCost: ''
}));

const mapUtilityRows = (rows) => {
    const byMonth = new Map((rows || []).map((r) => [Number(r.month), r]));
    return Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        const row = byMonth.get(month);
        return {
            month,
            waterCost: row && row.waterCost !== undefined ? String(row.waterCost) : '',
            gasCost: row && row.gasCost !== undefined ? String(row.gasCost) : '',
            energyCost: row && row.energyCost !== undefined ? String(row.energyCost) : ''
        };
    });
};

const SiteFeaturesPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();
    const [property, setProperty] = useState(null);
    const [listingHeroImageUrl, setListingHeroImageUrl] = useState('');
    const [features, setFeatures] = useState([]);
    const [propertyFeedItems, setPropertyFeedItems] = useState([]);
    const [showCostModal, setShowCostModal] = useState(false);
    const [showInvestmentModal, setShowInvestmentModal] = useState(false);
    const [showFeaturesModal, setShowFeaturesModal] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [showPricingRulesModal, setShowPricingRulesModal] = useState(false);
    const [showFeedEntryModal, setShowFeedEntryModal] = useState(false);
    const [feedEntryIsBooking, setFeedEntryIsBooking] = useState(false);
    const [editingBookingId, setEditingBookingId] = useState(null);
    const [form, setForm] = useState({ name: '', value: '' });
    const [editingFeatureId, setEditingFeatureId] = useState(null);
    const [costForm, setCostForm] = useState({ neighborhood: '', rentCost: '', acquisitionDate: '', rentStartDate: '', wifiCost: '', cleaningCost: '', suppliesCost: '', otherCost: '', pricingToolsCost: '', latitude: '', longitude: '' });
    const [investments, setInvestments] = useState([]);
    const [investmentSummary, setInvestmentSummary] = useState({ totalInvestment: 0, totalRecordedNet: 0, count: 0 });
    const [investmentForm, setInvestmentForm] = useState({ cost: '', reason: '', investedBy: '' });
    const [editingInvestmentId, setEditingInvestmentId] = useState(null);
    const [investmentSuccessMessage, setInvestmentSuccessMessage] = useState('');
    const [appMessage, setAppMessage] = useState({ type: '', text: '' });

    const [notes, setNotes] = useState([]);
    const [noteForm, setNoteForm] = useState({ title: '', note: '' });
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [receiptForm, setReceiptForm] = useState({ propertyId: '' });
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvRows, setCsvRows] = useState([]);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvError, setCsvError] = useState('');
    const [cohosts, setCohosts] = useState([]);
    const [cohostEmail, setCohostEmail] = useState('');
    const [cohostRole, setCohostRole] = useState('owner_coowner');
    const [cohostLoading, setCohostLoading] = useState(false);
    const [availableHosts, setAvailableHosts] = useState([]);
    const [availableHostsLoading, setAvailableHostsLoading] = useState(false);
    const [cohostDrafts, setCohostDrafts] = useState({});
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [payoutSplitRows, setPayoutSplitRows] = useState([]);
    const [payoutBeneficiaries, setPayoutBeneficiaries] = useState([]);
    const [payoutConflicts, setPayoutConflicts] = useState([]);
    const [payoutHistory, setPayoutHistory] = useState([]);
    const [payoutLoading, setPayoutLoading] = useState(false);

    const [maintenanceRecords, setMaintenanceRecords] = useState([]);
    const [maintenanceForm, setMaintenanceForm] = useState({ date: '', task: '', cost: '' });
    const [feedEntryForm, setFeedEntryForm] = useState({ amount: '', startDate: '', endDate: '', source: 'Airbnb', category: '', description: '', cleaningFee: '' });
    const [utilitiesYear, setUtilitiesYear] = useState(new Date().getFullYear());
    const [utilityRows, setUtilityRows] = useState(defaultUtilityRows());
    const [utilitiesLoading, setUtilitiesLoading] = useState(false);

    const formatCurrency = (value) => '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
    const formatDate = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '-';
        return parsed.toLocaleDateString();
    };
    const getProfitability = (summary) => ((Number(summary?.totalInvestment) || 0) - (Number(summary?.totalRecordedNet) || 0)) * -1;

    const load = async () => {
        // Load property details
        const propertyIdNumber = parseInt(propertyId, 10);
        const propRes = await api.get('/property');
        if (propRes && propRes.data) {
            const currentProperty = propRes.data.find(p => p.id === propertyIdNumber);
            setProperty(currentProperty);
            if (currentProperty) {
                setCostForm({
                    neighborhood: currentProperty.neighborhood || '',
                    rentCost: currentProperty.rentCost || '',
                    acquisitionDate: currentProperty.acquisitionDate || '',
                    rentStartDate: currentProperty.rentStartDate || '',
                    wifiCost: currentProperty.wifiCost || '',
                    cleaningCost: currentProperty.cleaningCost || '',
                    suppliesCost: currentProperty.suppliesCost || '',
                    otherCost: currentProperty.otherCost || '',
                    pricingToolsCost: currentProperty.pricingToolsCost || '',
                    latitude: currentProperty.latitude ?? '',
                    longitude: currentProperty.longitude ?? ''
                });
            }
        }

        try {
            const listingRes = await api.get(`/direct-booking/host/listings/property/${propertyIdNumber}`);
            setListingHeroImageUrl(String(listingRes?.data?.heroImageUrl || '').trim());
        } catch (e) {
            setListingHeroImageUrl('');
        }

        // Load features for this property
        const featRes = await api.get(`/siteFeature/property/${propertyId}`);
        if (featRes && featRes.data) setFeatures(featRes.data);

        await loadPropertyFeed();
        await loadNotes();
        await loadInvestments();
        await loadInvestmentSummary();
        await loadUtilityCharges();
        await loadCohosts();
        await loadAvailableHosts();
        await loadPayoutSplits();
    };

    const loadCohosts = async () => {
        if (!propertyId) return;
        try {
            const response = await api.get(`/property/${propertyId}/cohosts`);
            const rows = Array.isArray(response?.data) ? response.data : [];
            setCohosts(rows);
            setCohostDrafts(rows.reduce((acc, entry) => {
                if (entry?.isOwner) return acc;
                acc[entry.id] = {
                    role: entry.hostRole || 'view',
                    accessStatus: entry.accessStatus || 'active',
                    allow: Array.isArray(entry?.permissionOverrides?.allow) ? entry.permissionOverrides.allow : [],
                    deny: Array.isArray(entry?.permissionOverrides?.deny) ? entry.permissionOverrides.deny : []
                };
                return acc;
            }, {}));
        } catch (e) {
            setCohosts([]);
            console.error('failed to load cohosts', e);
        }
    };

    const togglePermissionOverride = (cohostUserId, mode, capabilityKey) => {
        setCohostDrafts((prev) => {
            const current = prev[cohostUserId] || { role: 'view', accessStatus: 'active', allow: [], deny: [] };
            const allowSet = new Set(current.allow || []);
            const denySet = new Set(current.deny || []);

            if (mode === 'allow') {
                if (allowSet.has(capabilityKey)) {
                    allowSet.delete(capabilityKey);
                } else {
                    allowSet.add(capabilityKey);
                    denySet.delete(capabilityKey);
                }
            }

            if (mode === 'deny') {
                if (denySet.has(capabilityKey)) {
                    denySet.delete(capabilityKey);
                } else {
                    denySet.add(capabilityKey);
                    allowSet.delete(capabilityKey);
                }
            }

            return {
                ...prev,
                [cohostUserId]: {
                    ...current,
                    allow: Array.from(allowSet),
                    deny: Array.from(denySet)
                }
            };
        });
    };

    const saveCohostPermissions = async (cohostUserId) => {
        const draft = cohostDrafts[cohostUserId];
        if (!draft) return;

        setCohostLoading(true);
        try {
            await api.put(`/property/${propertyId}/cohosts/${cohostUserId}`, {
                role: draft.role,
                accessStatus: draft.accessStatus,
                permissionOverrides: {
                    allow: draft.allow,
                    deny: draft.deny
                }
            });

            await loadCohosts();
            setAppMessage({ type: 'success', text: 'Co-host permissions updated.' });
        } catch (e) {
            setAppMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to update co-host permissions.' });
        } finally {
            setCohostLoading(false);
        }
    };

    const applyPermissionPreset = (cohostUserId, presetKey) => {
        const preset = PRESET_OPTIONS.find((entry) => entry.key === presetKey);
        if (!preset) return;

        setCohostDrafts((prev) => {
            const current = prev[cohostUserId] || { role: 'view', accessStatus: 'active', allow: [], deny: [] };
            const next = preset.apply();
            return {
                ...prev,
                [cohostUserId]: {
                    ...current,
                    ...next
                }
            };
        });
    };

    const loadPayoutSplits = async () => {
        if (!propertyId) return;
        setPayoutLoading(true);
        try {
            const response = await api.get(`/payout-split/property/${propertyId}`);
            const payload = response?.data || {};
            const beneficiaries = Array.isArray(payload.beneficiaries) ? payload.beneficiaries : [];
            const splits = Array.isArray(payload.splits) ? payload.splits : [];
            const conflicts = Array.isArray(payload.conflicts) ? payload.conflicts : [];

            setPayoutBeneficiaries(beneficiaries);
            setPayoutConflicts(conflicts);
            setPayoutSplitRows(splits.length > 0 ? splits.map((entry) => ({
                beneficiaryUserId: String(entry.beneficiaryUserId || ''),
                splitPercent: String(entry.splitPercent || ''),
                effectiveStartDate: entry.effectiveStartDate || '',
                effectiveEndDate: entry.effectiveEndDate || '',
                notes: entry.notes || ''
            })) : [
                {
                    beneficiaryUserId: String(beneficiaries?.[0]?.id || ''),
                    splitPercent: '',
                    effectiveStartDate: formatLocalDate(new Date()),
                    effectiveEndDate: '',
                    notes: ''
                }
            ]);

            try {
                const historyResponse = await api.get(`/payout-split/property/${propertyId}/history`);
                setPayoutHistory(Array.isArray(historyResponse?.data) ? historyResponse.data : []);
            } catch (_) {
                setPayoutHistory([]);
            }
        } catch (e) {
            if (e?.response?.status !== 403) {
                console.error('failed to load payout splits', e);
            }
            setPayoutBeneficiaries([]);
            setPayoutSplitRows([]);
            setPayoutConflicts([]);
            setPayoutHistory([]);
        } finally {
            setPayoutLoading(false);
        }
    };

    const addPayoutSplitRow = () => {
        setPayoutSplitRows((prev) => ([
            ...prev,
            {
                beneficiaryUserId: '',
                splitPercent: '',
                effectiveStartDate: formatLocalDate(new Date()),
                effectiveEndDate: '',
                notes: ''
            }
        ]));
    };

    const removePayoutSplitRow = (index) => {
        setPayoutSplitRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    };

    const onPayoutSplitChange = (index, field, value) => {
        setPayoutSplitRows((prev) => prev.map((row, rowIndex) => {
            if (rowIndex !== index) return row;
            return { ...row, [field]: value };
        }));
    };

    const savePayoutSplits = async () => {
        const normalizedRows = payoutSplitRows.map((entry) => ({
            beneficiaryUserId: Number(entry.beneficiaryUserId),
            splitPercent: Number(entry.splitPercent),
            effectiveStartDate: String(entry.effectiveStartDate || '').trim(),
            effectiveEndDate: String(entry.effectiveEndDate || '').trim() || null,
            notes: String(entry.notes || '').trim()
        }));

        if (normalizedRows.length === 0 || normalizedRows.some((entry) => !entry.beneficiaryUserId || !entry.splitPercent || !entry.effectiveStartDate)) {
            setAppMessage({ type: 'error', text: 'Each payout split row requires beneficiary, percent, and effective start date.' });
            return;
        }

        const localConflicts = getPayoutSplitConflicts(normalizedRows);
        if (localConflicts.length > 0) {
            setPayoutConflicts(localConflicts);
            setAppMessage({ type: 'error', text: 'Resolve payout split conflicts before saving.' });
            return;
        }

        setPayoutLoading(true);
        try {
            const response = await api.put(`/payout-split/property/${propertyId}`, {
                splits: normalizedRows
            });
            setPayoutConflicts(Array.isArray(response?.data?.conflicts) ? response.data.conflicts : []);
            await loadPayoutSplits();
            setAppMessage({ type: 'success', text: 'Payout split settings saved.' });
        } catch (e) {
            setAppMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to save payout splits.' });
        } finally {
            setPayoutLoading(false);
        }
    };

    const loadAvailableHosts = async (search = '') => {
        if (!propertyId) return;
        setAvailableHostsLoading(true);
        try {
            const encoded = encodeURIComponent(String(search || '').trim());
            const response = await api.get(`/property/${propertyId}/available-hosts?search=${encoded}`);
            setAvailableHosts(Array.isArray(response?.data) ? response.data : []);
        } catch (_) {
            setAvailableHosts([]);
        } finally {
            setAvailableHostsLoading(false);
        }
    };

    const loadPropertyFeed = async () => {
        if (!propertyId) return;

        try {
            const [transactionRes, bookingRes] = await Promise.all([
                api.get(`/transaction/property/${propertyId}`),
                api.get(`/booking/property/${propertyId}`)
            ]);

            const feedItems = [
                ...((transactionRes?.data || []).map((item) => ({ ...item, type: 'transaction' }))),
                ...((bookingRes?.data || []).map((item) => ({ ...item, type: 'booking' })))
            ];

            feedItems.sort((left, right) => {
                const leftDate = left.type === 'booking' ? left.startDate : left.date;
                const rightDate = right.type === 'booking' ? right.startDate : right.date;
                return new Date(rightDate || 0).getTime() - new Date(leftDate || 0).getTime();
            });

            setPropertyFeedItems(feedItems);
        } catch (e) {
            console.error('failed to load property feed', e);
            setPropertyFeedItems([]);
        }
    };

    const loadNotes = async () => {
        if (!propertyId) return;
        try {
            const noteRes = await api.get(`/note/property/${propertyId}`);
            if (noteRes && Array.isArray(noteRes.data)) {
                setNotes(noteRes.data);
            } else {
                setNotes([]);
            }
        } catch (e) {
            console.error('failed to load notes', e);
            setNotes([]);
        }
    };

    const loadInvestments = async () => {
        if (!propertyId) return;
        try {
            const investmentRes = await api.get(`/investment/property/${propertyId}`);
            if (investmentRes && Array.isArray(investmentRes.data)) {
                setInvestments(investmentRes.data);
            } else {
                setInvestments([]);
            }
        } catch (e) {
            console.error('failed to load investments', e);
            setInvestments([]);
        }
    };

    const loadInvestmentSummary = async () => {
        if (!propertyId) return;
        try {
            const response = await api.get(`/report/investments/${propertyId}`);
            if (response && response.data) {
                setInvestmentSummary({
                    totalInvestment: Number(response.data.totalInvestment) || 0,
                    totalRecordedNet: Number(response.data.totalRecordedNet) || 0,
                    count: Number(response.data.count) || 0
                });
            } else {
                setInvestmentSummary({ totalInvestment: 0, totalRecordedNet: 0, count: 0 });
            }
        } catch (e) {
            console.error('failed to load investment summary', e);
            setInvestmentSummary({ totalInvestment: 0, totalRecordedNet: 0, count: 0 });
        }
    };

    const loadUtilityCharges = async () => {
        if (!propertyId) return;
        setUtilitiesLoading(true);
        try {
            const utilRes = await api.get(`/utilityCharge/property/${propertyId}?year=${utilitiesYear}`);
            if (utilRes && utilRes.data && Array.isArray(utilRes.data.months)) {
                setUtilityRows(mapUtilityRows(utilRes.data.months));
            } else {
                setUtilityRows(defaultUtilityRows());
            }
        } catch (e) {
            console.error('failed to load utility charges', e);
            setUtilityRows(defaultUtilityRows());
        } finally {
            setUtilitiesLoading(false);
        }
    };

    useEffect(() => { 
        if (propertyId) {
            load();
        }
    }, [propertyId]);

    useEffect(() => {
        if (location.state?.geocodeWarning) {
            setAppMessage({ type: 'error', text: location.state.geocodeWarning });
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    useEffect(() => {
        if (propertyId) {
            loadUtilityCharges();
            setReceiptForm({ propertyId: String(propertyId) });
        }
    }, [propertyId, utilitiesYear]);

    useEffect(() => {
        if (!investmentSuccessMessage) return undefined;

        const timeoutId = window.setTimeout(() => {
            setInvestmentSuccessMessage('');
        }, 3000);

        return () => window.clearTimeout(timeoutId);
    }, [investmentSuccessMessage]);

    useEffect(() => {
        if (!appMessage.text) return undefined;

        const timeoutId = window.setTimeout(() => {
            setAppMessage({ type: '', text: '' });
        }, 3000);

        return () => window.clearTimeout(timeoutId);
    }, [appMessage]);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const onFeedEntryChange = (e) => setFeedEntryForm({ ...feedEntryForm, [e.target.name]: e.target.value });
    const onFeedDateRangeChange = (dates) => {
        const [start, end] = dates;
        setFeedEntryForm((prev) => ({
            ...prev,
            startDate: formatLocalDate(start),
            endDate: formatLocalDate(end)
        }));
    };

    const openReceiptModal = () => {
        setReceiptForm({ propertyId: String(propertyId || '') });
        setShowReceiptModal(true);
    };

    const closeReceiptModal = () => {
        setReceiptForm({ propertyId: String(propertyId || '') });
        setShowReceiptModal(false);
    };

    const openCsvModal = () => {
        setCsvRows([]);
        setCsvError('');
        setShowCsvModal(true);
    };

    const closeCsvModal = () => {
        setCsvRows([]);
        setCsvError('');
        setCsvImporting(false);
        setShowCsvModal(false);
    };

    const parseCsvText = (text) => {
        const cleaned = text.replace(/^\uFEFF/, '');
        const lines = cleaned.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length < 2) {
            return { rows: [], error: 'CSV appears to be empty.' };
        }

        const parseLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i += 1) {
                const char = line[i];
                if (char === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                        current += '"';
                        i += 1;
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }

            result.push(current.trim());
            return result;
        };

        const normalizeHeader = (value) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
        const headers = parseLine(lines[0]).map(normalizeHeader);

        const findColumn = (...names) => {
            for (const name of names) {
                const index = headers.indexOf(normalizeHeader(name));
                if (index !== -1) return index;
            }
            return -1;
        };

        const typeIndex = findColumn('type');
        const startIndex = findColumn('start date', 'startdate', 'check-in', 'checkin', 'check in');
        const endIndex = findColumn('end date', 'enddate', 'check-out', 'checkout', 'check out');
        const nightsIndex = findColumn('nights', 'num nights', 'numofnights');
        const amountIndex = findColumn('amount');
        const paidOutIndex = findColumn('paid out', 'paidout');
        const guestIndex = findColumn('guest', 'guest name', 'guestname');
        const confirmationIndex = findColumn('confirmation code', 'confirmationcode', 'confirm');

        if (startIndex === -1) {
            return { rows: [], error: 'Could not find a check-in date column.' };
        }

        if (amountIndex === -1 && paidOutIndex === -1) {
            return { rows: [], error: 'Could not find an amount column.' };
        }

        const parseAmount = (value) => {
            if (!value) return null;
            const parsed = parseFloat(String(value).replace(/[$,\s]/g, ''));
            return Number.isNaN(parsed) ? null : parsed;
        };

        const parseDate = (value) => {
            if (!value) return null;
            const trimmed = String(value).trim();
            const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (slashMatch) {
                return `${slashMatch[3]}-${slashMatch[1].padStart(2, '0')}-${slashMatch[2].padStart(2, '0')}`;
            }
            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                return trimmed;
            }
            return null;
        };

        const rows = [];
        for (let i = 1; i < lines.length; i += 1) {
            const columns = parseLine(lines[i]);
            if (columns.length < 2) continue;

            if (typeIndex !== -1) {
                const type = String(columns[typeIndex] || '').toLowerCase();
                if (type !== 'reservation') continue;
            }

            const startDate = parseDate(columns[startIndex]);
            if (!startDate) continue;

            let endDate = endIndex !== -1 ? parseDate(columns[endIndex]) : null;
            if (!endDate && nightsIndex !== -1) {
                const nights = parseInt(columns[nightsIndex], 10);
                if (!Number.isNaN(nights) && nights > 0) {
                    const end = new Date(`${startDate}T00:00:00`);
                    end.setDate(end.getDate() + nights);
                    endDate = formatLocalDate(end);
                }
            }
            if (!endDate) continue;

            const amount = parseAmount(amountIndex !== -1 ? columns[amountIndex] : null)
                || parseAmount(paidOutIndex !== -1 ? columns[paidOutIndex] : null);
            if (!amount || amount <= 0) continue;

            const descriptionParts = [];
            if (guestIndex !== -1 && columns[guestIndex]) descriptionParts.push(columns[guestIndex]);
            if (confirmationIndex !== -1 && columns[confirmationIndex]) descriptionParts.push(columns[confirmationIndex]);

            rows.push({
                startDate,
                endDate,
                amount,
                source: 'Airbnb',
                description: descriptionParts.join(' — ')
            });
        }

        return { rows, error: null };
    };

    const handleCsvFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        setCsvRows([]);
        setCsvError('');

        const reader = new FileReader();
        reader.onload = (event) => {
            const { rows, error } = parseCsvText(event.target?.result || '');
            if (error) {
                setCsvError(error);
                return;
            }
            if (rows.length === 0) {
                setCsvError('No importable booking rows found in this CSV.');
                return;
            }
            setCsvRows(rows);
        };
        reader.readAsText(file);
    };

    const importCsvBookings = async () => {
        if (!propertyId) {
            setCsvError('Property is missing.');
            return;
        }
        if (csvRows.length === 0) {
            setCsvError('No rows to import.');
            return;
        }

        setCsvImporting(true);
        setCsvError('');

        try {
            const res = await api.post('/booking/import', {
                propertyId: Number(propertyId),
                rows: csvRows
            });

            const inserted = Number(res?.data?.inserted) || 0;
            const errorCount = Array.isArray(res?.data?.errors) ? res.data.errors.length : 0;

            await loadPropertyFeed();
            closeCsvModal();
            setAppMessage({
                type: 'success',
                text: `Imported ${inserted} booking${inserted === 1 ? '' : 's'}${errorCount > 0 ? ` (${errorCount} skipped)` : ''}.`
            });
        } catch (e) {
            console.error('failed to import bookings', e);
            setCsvError(`Import failed: ${e?.response?.data?.error || e.message || 'unknown error'}`);
        } finally {
            setCsvImporting(false);
        }
    };

    const handleReceiptUpload = (e) => {
        const file = e.target.files[0];
        if (file && receiptForm.propertyId) {
            console.log('Receipt uploaded:', file.name, 'for property:', property?.name);
            setReceiptForm({ propertyId: String(propertyId || '') });
            setShowReceiptModal(false);
            setAppMessage({ type: 'success', text: 'Receipt uploaded successfully.' });
        } else {
            setAppMessage({ type: 'error', text: 'Unable to upload receipt. Please try again.' });
        }
    };

    const closeFeedEntryModal = () => {
        setShowFeedEntryModal(false);
        setFeedEntryIsBooking(false);
        setEditingBookingId(null);
        setFeedEntryForm({ amount: '', startDate: '', endDate: '', source: 'Airbnb', category: '', description: '', cleaningFee: '' });
    };

    const openBookingModal = () => {
        setFeedEntryIsBooking(true);
        setEditingBookingId(null);
        setFeedEntryForm({ amount: '', startDate: '', endDate: '', source: 'Airbnb', category: '', description: '', cleaningFee: '' });
        setShowFeedEntryModal(true);
    };

    const openBookingEditModal = (booking) => {
        setFeedEntryIsBooking(true);
        setEditingBookingId(Number(booking.id));
        setFeedEntryForm({
            amount: booking.amount !== undefined ? String(booking.amount) : '',
            startDate: booking.startDate || '',
            endDate: booking.endDate || '',
            source: booking.source || 'Airbnb',
            category: '',
            description: booking.description || '',
            cleaningFee: booking.cleaningFee !== undefined && booking.cleaningFee !== null ? String(booking.cleaningFee) : ''
        });
        setShowFeedEntryModal(true);
    };

    const deleteBooking = async (booking) => {
        if (!window.confirm('Are you sure you want to delete this booking?')) return;

        try {
            await api.delete(`/booking/${booking.id}`);
            await loadPropertyFeed();
            if (editingBookingId && Number(editingBookingId) === Number(booking.id)) {
                closeFeedEntryModal();
            }
            setAppMessage({ type: 'success', text: 'Booking deleted successfully.' });
        } catch (error) {
            console.error('failed to delete booking', error);
            setAppMessage({ type: 'error', text: 'Failed to delete booking; please try again.' });
        }
    };

    const openTransactionModal = () => {
        setFeedEntryIsBooking(false);
        setFeedEntryForm({ amount: '', startDate: formatLocalDate(new Date()), endDate: '', source: '', category: '', description: '' });
        setShowFeedEntryModal(true);
    };

    const onFeedEntrySubmit = async (e) => {
        e.preventDefault();

        if (feedEntryIsBooking) {
            const start = new Date(feedEntryForm.startDate);
            const end = new Date(feedEntryForm.endDate);
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
                setAppMessage({ type: 'error', text: 'Check-out date must be after check-in date.' });
                return;
            }
        }

        try {
            const payload = feedEntryIsBooking
                ? {
                    propertyId: Number(propertyId),
                    amount: Number(feedEntryForm.amount),
                    startDate: feedEntryForm.startDate,
                    endDate: feedEntryForm.endDate,
                    source: feedEntryForm.source,
                    description: feedEntryForm.description,
                    cleaningFee: feedEntryForm.cleaningFee === '' ? null : Number(feedEntryForm.cleaningFee)
                }
                : {
                    propertyId: Number(propertyId),
                    amount: Number(feedEntryForm.amount),
                    date: feedEntryForm.startDate || formatLocalDate(new Date()),
                    category: feedEntryForm.category,
                    description: feedEntryForm.description
                };

            if (feedEntryIsBooking && editingBookingId) {
                await api.put(`/booking/${editingBookingId}`, payload);
            } else {
                await api.post(feedEntryIsBooking ? '/booking' : '/transaction', payload);
            }
            await loadPropertyFeed();
            closeFeedEntryModal();
            if (feedEntryIsBooking && editingBookingId) {
                setAppMessage({ type: 'success', text: 'Booking updated successfully.' });
            } else {
                setAppMessage({ type: 'success', text: `${feedEntryIsBooking ? 'Booking' : 'Transaction'} saved successfully.` });
            }
        } catch (error) {
            console.error('failed to save property feed item', error);
            setAppMessage({ type: 'error', text: `Failed to save ${feedEntryIsBooking ? 'booking' : 'transaction'}; please try again.` });
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingFeatureId) {
                await api.put(`/siteFeature/${editingFeatureId}`, form);
            } else {
                await api.post('/siteFeature', { ...form, propertyId: parseInt(propertyId, 10) });
            }
            setForm({ name: '', value: '' });
            setEditingFeatureId(null);
            load();
        } catch (e) {
            console.error('failed to save feature', e);
            setAppMessage({ type: 'error', text: 'Failed to save feature; please try again.' });
        }
    };

    const onNoteChange = (e) => setNoteForm({ ...noteForm, [e.target.name]: e.target.value });
    const onNoteSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingNoteId) {
                await api.put(`/note/${editingNoteId}`, noteForm);
            } else {
                await api.post(`/note/property/${propertyId}`, noteForm);
            }
            setNoteForm({ title: '', note: '' });
            setEditingNoteId(null);
            await loadNotes();
        } catch (e) {
            console.error('failed to save note', e);
            setAppMessage({ type: 'error', text: 'Failed to save note; please try again.' });
        }
    };

    const onMaintenanceChange = (e) => setMaintenanceForm({ ...maintenanceForm, [e.target.name]: e.target.value });
    const onMaintenanceSubmit = (e) => {
        e.preventDefault();
        setMaintenanceRecords([...maintenanceRecords, { ...maintenanceForm, id: Date.now() }]);
        setMaintenanceForm({ date: '', task: '', cost: '' });
    };

    const onCostChange = (e) => setCostForm({ ...costForm, [e.target.name]: e.target.value });

    const toggleActiveListing = async () => {
        if (!property) return;
        const next = !property.isActiveListing;
        try {
            const res = await api.put(`/property/${propertyId}`, { isActiveListing: next });
            if (res && res.data) setProperty(res.data);
            setAppMessage({ type: 'success', text: next ? 'Property is now an active public listing.' : 'Property removed from public listings.' });
        } catch (e) {
            console.error('failed to toggle active listing', e);
            setAppMessage({ type: 'error', text: 'Failed to update listing status.' });
        }
    };
    const onInvestmentChange = (e) => setInvestmentForm({ ...investmentForm, [e.target.name]: e.target.value });

    const openInvestmentModal = () => {
        setInvestmentSuccessMessage('');
        setShowInvestmentModal(true);
    };

    const closeInvestmentModal = () => {
        setShowInvestmentModal(false);
        setInvestmentSuccessMessage('');
        cancelInvestmentEdit();
    };

    const startFeatureEdit = (feature) => {
        setEditingFeatureId(feature.id);
        setForm({ name: feature.name || '', value: feature.value || '' });
    };

    const cancelFeatureEdit = () => {
        setEditingFeatureId(null);
        setForm({ name: '', value: '' });
    };

    const deleteFeature = async (featureId) => {
        if (!window.confirm('Delete this property feature?')) return;
        try {
            await api.delete(`/siteFeature/${featureId}`);
            if (editingFeatureId === featureId) {
                cancelFeatureEdit();
            }
            await load();
        } catch (e) {
            console.error('failed to delete feature', e);
            setAppMessage({ type: 'error', text: 'Failed to delete feature; please try again.' });
        }
    };

    const startNoteEdit = (note) => {
        setEditingNoteId(note.id);
        setNoteForm({ title: note.title || '', note: note.note || '' });
    };

    const cancelNoteEdit = () => {
        setEditingNoteId(null);
        setNoteForm({ title: '', note: '' });
    };

    const deleteNote = async (noteId) => {
        if (!window.confirm('Delete this property note?')) return;
        try {
            await api.delete(`/note/${noteId}`);
            if (editingNoteId === noteId) {
                cancelNoteEdit();
            }
            await loadNotes();
        } catch (e) {
            console.error('failed to delete note', e);
            setAppMessage({ type: 'error', text: 'Failed to delete note; please try again.' });
        }
    };

    const startInvestmentEdit = (investment) => {
        setInvestmentSuccessMessage('');
        setEditingInvestmentId(investment.id);
        setInvestmentForm({
            cost: investment.cost !== undefined ? String(investment.cost) : '',
            reason: investment.reason || '',
            investedBy: investment.investedBy || ''
        });
    };

    const cancelInvestmentEdit = () => {
        setEditingInvestmentId(null);
        setInvestmentForm({ cost: '', reason: '' });
    };

    const deleteInvestment = async (investmentId) => {
        if (!window.confirm('Delete this investment?')) return;
        try {
            await api.delete(`/investment/${investmentId}`);
            if (editingInvestmentId === investmentId) {
                cancelInvestmentEdit();
            }
            await loadInvestments();
        } catch (e) {
            console.error('failed to delete investment', e);
            setAppMessage({ type: 'error', text: 'Failed to delete investment; please try again.' });
        }
    };

    const addCohost = async (e) => {
        e.preventDefault();
        const normalizedEmail = String(cohostEmail || '').trim().toLowerCase();
        if (!normalizedEmail) {
            setAppMessage({ type: 'error', text: 'Please enter a co-host email.' });
            return;
        }

        setCohostLoading(true);
        try {
            await api.post(`/property/${propertyId}/cohosts`, {
                email: normalizedEmail,
                role: cohostRole
            });
            setCohostEmail('');
            await loadCohosts();
            await loadAvailableHosts();
            setAppMessage({ type: 'success', text: 'Co-host added or invite sent.' });
        } catch (error) {
            setAppMessage({ type: 'error', text: error?.response?.data?.error || 'Failed to add co-host.' });
        } finally {
            setCohostLoading(false);
        }
    };

    const removeCohost = async (cohostUserId) => {
        if (!window.confirm('Remove this co-host from the property?')) return;
        setCohostLoading(true);
        try {
            await api.delete(`/property/${propertyId}/cohosts/${cohostUserId}`);
            await loadCohosts();
            await loadAvailableHosts();
            setAppMessage({ type: 'success', text: 'Co-host removed.' });
        } catch (error) {
            setAppMessage({ type: 'error', text: error?.response?.data?.error || 'Failed to remove co-host.' });
        } finally {
            setCohostLoading(false);
        }
    };

    const onUtilityChange = (month, field, value) => {
        setUtilityRows((prev) => prev.map((row) => {
            if (row.month !== month) return row;
            return { ...row, [field]: value };
        }));
    };

    const onUtilitySave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                year: Number(utilitiesYear),
                months: utilityRows.map((row) => ({
                    month: row.month,
                    waterCost: parseFloat(row.waterCost) || 0,
                    gasCost: parseFloat(row.gasCost) || 0,
                    energyCost: parseFloat(row.energyCost) || 0
                }))
            };
            const res = await api.post(`/utilityCharge/property/${propertyId}`, payload);
            if (res && res.data && Array.isArray(res.data.months)) {
                setUtilityRows(mapUtilityRows(res.data.months));
                setAppMessage({ type: 'success', text: 'Monthly utility charges saved successfully.' });
            }
        } catch (e) {
            console.error('failed to save utility charges', e);
            setAppMessage({ type: 'error', text: 'Failed to save utility charges; please try again.' });
        }
    };

    const onCostSave = async (e) => {
        e.preventDefault();
        if (!property) return;
        try {
            const update = {
                neighborhood: String(costForm.neighborhood || '').trim(),
                rentCost: parseFloat(costForm.rentCost) || 0,
                acquisitionDate: costForm.acquisitionDate || null,
                rentStartDate: costForm.rentStartDate || null,
                wifiCost: parseFloat(costForm.wifiCost) || 0,
                cleaningCost: parseFloat(costForm.cleaningCost) || 0,
                suppliesCost: parseFloat(costForm.suppliesCost) || 0,
                otherCost: parseFloat(costForm.otherCost) || 0,
                pricingToolsCost: parseFloat(costForm.pricingToolsCost) || 0,
                latitude: costForm.latitude === '' ? null : parseFloat(costForm.latitude),
                longitude: costForm.longitude === '' ? null : parseFloat(costForm.longitude)
            };
            const res = await api.put(`/property/${propertyId}`, update);
            if (res && res.data) {
                setProperty(res.data);
                setCostForm({
                    neighborhood: res.data.neighborhood || '',
                    rentCost: res.data.rentCost || '',
                    acquisitionDate: res.data.acquisitionDate || '',
                    rentStartDate: res.data.rentStartDate || '',
                    wifiCost: res.data.wifiCost || '',
                    cleaningCost: res.data.cleaningCost || '',
                    suppliesCost: res.data.suppliesCost || '',
                    otherCost: res.data.otherCost || '',
                    pricingToolsCost: res.data.pricingToolsCost || '',
                    latitude: res.data.latitude ?? '',
                    longitude: res.data.longitude ?? ''
                });
                setShowCostModal(false);
                setAppMessage(
                    res.data.geocodeWarning
                        ? { type: 'error', text: res.data.geocodeWarning }
                        : { type: 'success', text: 'Property cost settings saved successfully.' }
                );
            }
        } catch (error) {
            console.error('failed to save costs', error);
            setAppMessage({ type: 'error', text: 'Failed to save costs; please try again.' });
        }
    };

    const onInvestmentSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                cost: parseFloat(investmentForm.cost) || 0,
                reason: investmentForm.reason.trim(),
                investedBy: investmentForm.investedBy.trim()
            };
            if (editingInvestmentId) {
                await api.put(`/investment/${editingInvestmentId}`, payload);
            } else {
                await api.post(`/investment/property/${propertyId}`, payload);
            }
            setInvestmentForm({ cost: '', reason: '', investedBy: '' });
            setEditingInvestmentId(null);
            await loadInvestments();
            setInvestmentSuccessMessage(editingInvestmentId ? 'Investment updated successfully.' : 'Investment added successfully.');
        } catch (error) {
            console.error('failed to save investment', error);
            setAppMessage({ type: 'error', text: 'Failed to save investment; please try again.' });
        }
    };

    if (!property) {
        return <div>Loading...</div>;
    }

    const propertyInitials = (property.name || 'Property')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');

    const monthlyStandardTotal =
        (Number(property.rentCost) || 0) +
        (Number(property.wifiCost) || 0) +
        (Number(property.cleaningCost) || 0) +
        (Number(property.suppliesCost) || 0) +
        (Number(property.otherCost) || 0) +
        (Number(property.pricingToolsCost) || 0);

    const utilityYearTotal = utilityRows.reduce((sum, row) => {
        return sum + (Number(row.waterCost) || 0) + (Number(row.gasCost) || 0) + (Number(row.energyCost) || 0);
    }, 0);
    const totalInvestmentAmount = investments.reduce((sum, investment) => sum + (Number(investment.cost) || 0), 0);
    const profitability = getProfitability(investmentSummary);
    const latestNote = notes.length > 0 ? notes[0] : null;
    const latestMaintenance = maintenanceRecords.length > 0 ? maintenanceRecords[maintenanceRecords.length - 1] : null;

    const topFeatureChips = features.slice(0, 8);
    const profileCoverStyle = listingHeroImageUrl
        ? {
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.26), rgba(15, 23, 42, 0.26)), url(${listingHeroImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        }
        : undefined;
    const isPropertyOwner = Number(user?.id) > 0 && Number(property?.ownerUserId) === Number(user?.id);
    const selectedBookingStartDate = parseLocalDate(feedEntryForm.startDate);
    const selectedBookingEndDate = parseLocalDate(feedEntryForm.endDate);
    const bookingNights = selectedBookingStartDate && selectedBookingEndDate && selectedBookingEndDate > selectedBookingStartDate
        ? Math.round((selectedBookingEndDate.getTime() - selectedBookingStartDate.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    const bookingRangeSummary = selectedBookingStartDate && selectedBookingEndDate
        ? `${selectedBookingStartDate.toLocaleDateString()} - ${selectedBookingEndDate.toLocaleDateString()}`
        : selectedBookingStartDate
            ? `${selectedBookingStartDate.toLocaleDateString()} - Select check-out`
            : 'Select check-in and check-out dates';
    const currentCohostEntry = cohosts.find((entry) => Number(entry.id) === Number(user?.id));
    const currentCapabilities = new Set(
        (Array.isArray(currentCohostEntry?.capabilities) ? currentCohostEntry.capabilities : [])
            .map((cap) => String(cap || '').trim())
    );
    const canManageCohosts = isPropertyOwner || currentCapabilities.has('cohost.manage');
    const canReadPayouts = isPropertyOwner || currentCapabilities.has('payouts.read');
    const canWritePayouts = isPropertyOwner || currentCapabilities.has('payouts.write');
    const sortedCohosts = [...cohosts].sort((left, right) => {
        if (left.isOwner && !right.isOwner) return -1;
        if (!left.isOwner && right.isOwner) return 1;
        const leftName = String(left.display || left.email || '').toLowerCase();
        const rightName = String(right.display || right.email || '').toLowerCase();
        return leftName.localeCompare(rightName);
    });
    const filteredAvailableHosts = availableHosts.filter((entry) => {
        const q = String(cohostEmail || '').trim().toLowerCase();
        if (!q) return true;
        const email = String(entry.email || '').toLowerCase();
        const display = String(entry.display || '').toLowerCase();
        return email.includes(q) || display.includes(q);
    });

    return (
        <div className="property-profile-page">
            {appMessage.text && (
                <div className={`app-notice app-notice-fixed ${appMessage.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {appMessage.text}
                </div>
            )}
            <div className="profile-cover" style={profileCoverStyle}>
                <button className="btn-secondary back-btn" onClick={() => navigate('/Properties')}>
                    ← Back to Properties
                </button>
            </div>

            <div className="profile-header-card">
                <div className="profile-avatar">{propertyInitials || 'PR'}</div>
                <div className="profile-identity">
                    <h1>{property.name || 'Property Profile'}</h1>
                    <p>{property.address1}, {property.city}, {property.state} {property.postalCode || ''}</p>
                    {property.neighborhood && <p><strong>Neighborhood:</strong> {property.neighborhood}</p>}
                    <div className="profile-tags">
                        <span>{property.bedrooms || 0} Beds</span>
                        <span>{property.bathrooms || 0} Baths</span>
                    </div>
                    <div className="profile-tags profile-financial-tags">
                        <span className="profile-metric-chip">
                            Invested: <strong>{formatCurrency(investmentSummary.totalInvestment)}</strong>
                        </span>
                        <span className="profile-metric-chip">
                            P&amp;L To Date: <strong className={(Number(investmentSummary.totalRecordedNet) || 0) >= 0 ? 'net-positive' : 'net-negative'}>{formatCurrency(investmentSummary.totalRecordedNet)}</strong>
                        </span>
                        <span className="profile-metric-chip">
                            Profitability: <strong className={profitability >= 0 ? 'net-positive' : 'net-negative'}>{formatCurrency(profitability)}</strong>
                        </span>
                    </div>
                    <div className="profile-tags profile-feature-tags">
                        {topFeatureChips.length === 0 ? (
                            <span className="empty-feature-tag">No property features added yet</span>
                        ) : (
                            topFeatureChips.map((f) => (
                                <span key={f.id}>{f.name}: {f.value}</span>
                            ))
                        )}
                    </div>
                </div>
                <div className="profile-header-actions">
                    <div className="profile-header-action-stack">
                        <button
                            type="button"
                            className={property.isActiveListing ? 'btn-active-listing btn-active-listing-on' : 'btn-active-listing btn-active-listing-off'}
                            onClick={toggleActiveListing}
                            title={property.isActiveListing ? 'Click to remove from public listings' : 'Click to publish to public listings'}
                        >
                            {property.isActiveListing ? '● Active Listing' : '○ Not Listed Publicly'}
                        </button>
                        <button type="button" className="btn-primary" onClick={openCsvModal}>
                            Import Airbnb CSV
                        </button>
                        <button type="button" className="btn-secondary" onClick={openReceiptModal}>
                            Upload Receipt
                        </button>
                    </div>
                </div>
            </div>

            <section className="profile-quick-actions" aria-label="Host quick actions">
                <button type="button" className="profile-quick-action-card" onClick={() => setShowPricingRulesModal(true)}>
                    <span className="profile-quick-action-kicker">Pricing</span>
                    <strong>Dynamic Pricing</strong>
                    <span>Adjust nightly overrides without leaving this page.</span>
                </button>
                <button type="button" className="profile-quick-action-card" onClick={() => navigate(`/PropertyDetails/${propertyId}/bookings-calendar`)}>
                    <span className="profile-quick-action-kicker">Calendar</span>
                    <strong>Bookings Calendar</strong>
                    <span>Review occupancy, nightly value, and date coverage.</span>
                </button>
                <button type="button" className="profile-quick-action-card" onClick={() => navigate(`/PropertyDetails/${propertyId}/direct-booking#payout-settings`)}>
                    <span className="profile-quick-action-kicker">Payouts</span>
                    <strong>Stripe Payouts</strong>
                    <span>Open host-specific payout setup and account status.</span>
                </button>
                <button type="button" className="profile-quick-action-card" onClick={() => navigate(`/PropertyDetails/${propertyId}/utilities`)}>
                    <span className="profile-quick-action-kicker">Utilities</span>
                    <strong>Utility Charges</strong>
                    <span>Log monthly utility costs for this property.</span>
                </button>
                <button type="button" className="profile-quick-action-card" onClick={() => navigate(`/PropertyDetails/${propertyId}/direct-booking`)}>
                    <span className="profile-quick-action-kicker">Page</span>
                    <strong>Direct Booking Page</strong>
                    <span>Edit listing content, rules, and public booking settings.</span>
                </button>
            </section>

            <div className="profile-columns">
                <aside className="profile-sidebar">
                    <section className="profile-card">
                        <h3>Financial Reports</h3>
                        <div className="button-stack report-button-stack">
                            <button type="button" className="btn-primary" onClick={() => navigate(`/PropertyDetails/${propertyId}/investments`)}>
                                View Investment Report
                            </button>
                            <button type="button" className="btn-primary" onClick={() => navigate(`/PropertyDetails/${propertyId}/pnl`)}>
                                View P&amp;L Snapshot
                            </button>
                            {canReadPayouts && (
                                <button type="button" className="btn-primary" onClick={() => { loadPayoutSplits(); setShowPayoutModal(true); }}>
                                    Payout Split Settings
                                </button>
                            )}
                        </div>
                    </section>

                    <section className="profile-card">
                        <h3>Costs & Operations</h3>
                        <div className="button-stack">
                            <button type="button" className="btn-primary" onClick={() => setShowCostModal(true)}>
                                Edit Standard Costs
                            </button>
                            <button type="button" className="btn-primary" onClick={() => navigate(`/PropertyDetails/${propertyId}/supplies`)}>
                                View Supplies Schedule
                            </button>
                            <button type="button" className="btn-primary" onClick={openInvestmentModal}>
                                Manage Investments
                            </button>
                        </div>
                    </section>

                    <section className="profile-card">
                        <h3>Co-hosts ({cohosts.length})</h3>
                        <p className="cohost-help-text">The original listing creator manages host access for this property.</p>
                        {cohosts.length === 0 ? (
                            <p className="no-features">No hosts assigned yet.</p>
                        ) : (
                            <ul className="details-list cohost-list">
                                {sortedCohosts.map((entry) => (
                                    <li key={entry.id} className="details-item cohost-item">
                                        <div className="item-content">
                                            <strong>{entry.display || entry.email}</strong>
                                            <div className="cohost-email">{entry.email}</div>
                                        </div>
                                        <div className="item-actions">
                                            <span className={entry.isOwner ? 'cohost-owner-chip' : 'cohost-role-chip'}>{entry.isOwner ? 'Owner' : String(entry.hostRole || 'view').replace('_', ' ')}</span>
                                            {!entry.isOwner && canManageCohosts && (
                                                <button
                                                    type="button"
                                                    className="btn-secondary btn-inline btn-danger-inline"
                                                    onClick={() => removeCohost(entry.id)}
                                                    disabled={cohostLoading}
                                                >
                                                    {cohostLoading ? 'Removing...' : 'Remove'}
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {canManageCohosts ? (
                            <form className="feature-form cohost-form" onSubmit={addCohost}>
                                <label htmlFor="cohost-email" className="cohost-form-label">Add host by email</label>
                                <input
                                    id="cohost-email"
                                    type="email"
                                    list="available-host-email-list"
                                    placeholder="name@example.com"
                                    value={cohostEmail}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setCohostEmail(nextValue);
                                        if (nextValue.length === 0 || nextValue.length >= 2) {
                                            loadAvailableHosts(nextValue);
                                        }
                                    }}
                                    required
                                />
                                <label htmlFor="cohost-role" className="cohost-form-label">Role</label>
                                <select
                                    id="cohost-role"
                                    value={cohostRole}
                                    onChange={(e) => setCohostRole(e.target.value)}
                                >
                                    <option value="owner_coowner">Owner / Co-owner</option>
                                    <option value="cleaner">Cleaner</option>
                                    <option value="view">View only</option>
                                </select>
                                <datalist id="available-host-email-list">
                                    {filteredAvailableHosts.slice(0, 100).map((entry) => (
                                        <option key={entry.id} value={entry.email}>{entry.display || entry.email}</option>
                                    ))}
                                </datalist>
                                <p className="cohost-readonly-note">
                                    {availableHostsLoading
                                        ? 'Loading host emails...'
                                        : `Available hosts: ${filteredAvailableHosts.length}`}
                                </p>
                                <button type="submit" className="btn-primary" disabled={cohostLoading}>
                                    {cohostLoading ? 'Saving...' : 'Add Co-host'}
                                </button>
                            </form>
                        ) : (
                            <p className="cohost-readonly-note">You can view co-host assignments, but your role cannot edit them.</p>
                        )}
                    </section>

                    {canManageCohosts && (
                        <section className="profile-card">
                            <h3>Permission Matrix</h3>
                            {sortedCohosts.filter((entry) => !entry.isOwner).length === 0 ? (
                                <p className="no-features">No co-hosts to configure yet.</p>
                            ) : (
                                <div className="cohost-permissions-stack">
                                    {sortedCohosts.filter((entry) => !entry.isOwner).map((entry) => {
                                        const draft = cohostDrafts[entry.id] || { role: entry.hostRole || 'view', accessStatus: entry.accessStatus || 'active', allow: [], deny: [] };

                                        const statusMeta = STATUS_META[draft.accessStatus] || STATUS_META.active;
                                        const initials = (entry.display || entry.email || '?').slice(0, 2).toUpperCase();
                                        return (
                                            <div key={`permission-${entry.id}`} className="cohost-permission-card">

                                                {/* ── Card Header ── */}
                                                <div className="perm-card-header">
                                                    <div className="perm-avatar">{initials}</div>
                                                    <div className="perm-identity">
                                                        <strong>{entry.display || entry.email}</strong>
                                                        {entry.display && <span className="perm-email">{entry.email}</span>}
                                                    </div>
                                                    <div className="perm-badges">
                                                        <span className="perm-role-chip">{ROLE_LABELS[draft.role] || draft.role}</span>
                                                        <span className="perm-status-dot" style={{ background: statusMeta.color }} title={statusMeta.label} />
                                                    </div>
                                                </div>

                                                {/* ── Role / Status / Preset controls ── */}
                                                <div className="perm-controls-row">
                                                    <label className="perm-select-field">
                                                        <span>Role</span>
                                                        <select value={draft.role} onChange={(e) => setCohostDrafts((prev) => ({ ...prev, [entry.id]: { ...draft, role: e.target.value } }))}>
                                                            <option value="owner_coowner">Owner / Co-owner</option>
                                                            <option value="cleaner">Cleaner</option>
                                                            <option value="view">View only</option>
                                                        </select>
                                                    </label>
                                                    <label className="perm-select-field">
                                                        <span>Status</span>
                                                        <select value={draft.accessStatus} onChange={(e) => setCohostDrafts((prev) => ({ ...prev, [entry.id]: { ...draft, accessStatus: e.target.value } }))}>
                                                            <option value="active">Active</option>
                                                            <option value="paused">Paused</option>
                                                            <option value="removed">Removed</option>
                                                        </select>
                                                    </label>
                                                </div>

                                                {/* ── Preset pill buttons ── */}
                                                <div className="perm-presets">
                                                    <span className="perm-presets-label">Presets</span>
                                                    {PRESET_OPTIONS.map((preset) => (
                                                        <button
                                                            key={preset.key}
                                                            type="button"
                                                            className="perm-preset-pill"
                                                            onClick={() => applyPermissionPreset(entry.id, preset.key)}
                                                        >
                                                            {preset.label}
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* ── Capability toggle grid grouped by domain ── */}
                                                <div className="perm-capability-groups">
                                                    {CAPABILITY_GROUPS.map((group) => (
                                                        <div key={group.label} className="perm-cap-group">
                                                            <div className="perm-cap-group-label">{group.label}</div>
                                                            {group.keys.map((capKey) => {
                                                                const cap = CAPABILITY_OPTIONS.find((c) => c.key === capKey);
                                                                if (!cap) return null;
                                                                const isAllowed = (draft.allow || []).includes(capKey);
                                                                const isDenied = (draft.deny || []).includes(capKey);
                                                                const shortLabel = cap.label.replace(/(Read|Write|Manage)/, '').trim();
                                                                const access = cap.label.includes('Write') ? 'Write'
                                                                    : cap.label.includes('Read') ? 'Read'
                                                                    : 'Manage';
                                                                return (
                                                                    <div key={capKey} className="perm-cap-row">
                                                                        <span className="perm-cap-name">
                                                                            <span className="perm-cap-access">{access}</span>
                                                                        </span>
                                                                        <div className="perm-cap-toggles">
                                                                            <button
                                                                                type="button"
                                                                                className={`perm-toggle perm-toggle-allow${isAllowed ? ' perm-toggle-on' : ''}`}
                                                                                onClick={() => togglePermissionOverride(entry.id, 'allow', capKey)}
                                                                                title="Allow — grants this permission regardless of role"
                                                                            >
                                                                                Allow
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className={`perm-toggle perm-toggle-deny${isDenied ? ' perm-toggle-on' : ''}`}
                                                                                onClick={() => togglePermissionOverride(entry.id, 'deny', capKey)}
                                                                                title="Deny — blocks this permission regardless of role"
                                                                            >
                                                                                Deny
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="perm-save-row">
                                                    <button type="button" className="btn-primary" onClick={() => saveCohostPermissions(entry.id)} disabled={cohostLoading}>
                                                        {cohostLoading ? 'Saving...' : 'Save Permissions'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}

                    <section className="profile-card">
                        <h3>Property Info</h3>
                        <div className="button-stack">
                            <button type="button" className="btn-primary" onClick={() => setShowFeaturesModal(true)}>
                                Manage Features ({features.length})
                            </button>
                            <button type="button" className="btn-primary" onClick={() => setShowNotesModal(true)}>
                                Manage Notes ({notes.length})
                            </button>
                            <button type="button" className="btn-primary" onClick={() => setShowMaintenanceModal(true)}>
                                Maintenance Records ({maintenanceRecords.length})
                            </button>
                        </div>
                    </section>
                </aside>

                <main className="profile-main">
                    <PropertyFeed
                        items={propertyFeedItems}
                        onNewTransaction={openTransactionModal}
                        onNewBooking={openBookingModal}
                        onEditBooking={openBookingEditModal}
                        onDeleteBooking={deleteBooking}
                        defaultCleaningFee={property.cleaningCost}
                    />
                </main>
            </div>

            {showReceiptModal && (
                <div className="modal-overlay" onClick={closeReceiptModal}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upload Receipt</h3>
                            <button className="modal-close" onClick={closeReceiptModal}>&times;</button>
                        </div>
                        <div className="receipt-upload">
                            <div className="receipt-property-select">
                                <label>Property:</label>
                                <input value={property?.name || ''} disabled readOnly />
                            </div>
                            <input type="file" accept="image/*" onChange={handleReceiptUpload} style={{ display: 'none' }} id="property-receipt-file" />
                            <label htmlFor="property-receipt-file" className="upload-label">
                                <div className="upload-box">
                                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📸</div>
                                    <p>Click to select or drag and drop an image</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {showCsvModal && (
                <div className="modal-overlay" onClick={closeCsvModal}>
                    <div className="modal-content modal-content-wide property-modal-content property-modal-content-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Import Bookings from Airbnb CSV</h3>
                            <button className="modal-close" onClick={closeCsvModal}>&times;</button>
                        </div>
                        <div className="csv-import-setup">
                            <div className="csv-hint">
                                <p><strong>Use Airbnb Transaction History CSV.</strong> This importer pulls reservation rows into this property's booking feed.</p>
                                <ol>
                                    <li>In Airbnb, go to <strong>Earnings</strong>.</li>
                                    <li>Open <strong>Transaction History</strong>.</li>
                                    <li>Choose the date range or property if needed.</li>
                                    <li>Click <strong>Download CSV</strong> or <strong>Export CSV</strong>.</li>
                                    <li>Upload that file here.</li>
                                </ol>
                                <p>Only rows with <strong>Type = Reservation</strong> are imported. If the file is not transaction history, the importer may not find usable bookings.</p>
                            </div>
                            <div>
                                <label htmlFor="propertyCsvFile">CSV File</label>
                                <input
                                    id="propertyCsvFile"
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={handleCsvFile}
                                />
                            </div>
                        </div>

                        {csvError && <p className="csv-error">{csvError}</p>}

                        {csvRows.length > 0 && (
                            <div className="csv-preview">
                                <p className="csv-preview-info">{csvRows.length} booking{csvRows.length === 1 ? '' : 's'} ready to import for {property.name}:</p>
                                <div className="csv-preview-table-wrap">
                                    <table className="csv-preview-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Check-in</th>
                                                <th>Check-out</th>
                                                <th>Nights</th>
                                                <th>Amount</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {csvRows.map((row, index) => {
                                                const nights = Math.round((new Date(row.endDate) - new Date(row.startDate)) / (1000 * 60 * 60 * 24));
                                                return (
                                                    <tr key={`${row.startDate}-${row.endDate}-${index}`}>
                                                        <td>{index + 1}</td>
                                                        <td>{row.startDate}</td>
                                                        <td>{row.endDate}</td>
                                                        <td>{nights}</td>
                                                        <td>{formatCurrency(row.amount)}</td>
                                                        <td>{row.description || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="modal-buttons modal-buttons-right csv-modal-buttons">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={importCsvBookings}
                                disabled={csvImporting || csvRows.length === 0}
                            >
                                {csvImporting ? 'Importing...' : `Import ${csvRows.length > 0 ? csvRows.length : ''} Booking${csvRows.length === 1 ? '' : 's'}`}
                            </button>
                            <button type="button" className="btn-secondary" onClick={closeCsvModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {showFeedEntryModal && (
                <div className="modal-overlay" onClick={closeFeedEntryModal}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{feedEntryIsBooking ? (editingBookingId ? 'Edit Booking' : 'Add Booking') : 'Add Transaction'}</h3>
                            <button className="modal-close" onClick={closeFeedEntryModal}>&times;</button>
                        </div>
                        <form onSubmit={onFeedEntrySubmit} className="modal-form">
                            <label htmlFor="feedEntryProperty">Property</label>
                            <input id="feedEntryProperty" value={property.name || ''} disabled readOnly />

                            <label htmlFor="feedEntryAmount">Amount</label>
                            <input
                                id="feedEntryAmount"
                                name="amount"
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={feedEntryForm.amount}
                                onChange={onFeedEntryChange}
                                required
                            />

                            {feedEntryIsBooking ? (
                                <>
                                    <label htmlFor="feedEntryDateRange">Stay Dates</label>
                                    <div className="feed-entry-date-range-block">
                                        <div className="feed-entry-date-range-summary" id="feedEntryDateRange" aria-live="polite">
                                            <div className="feed-entry-date-range-copy">{bookingRangeSummary}</div>
                                            <div className="feed-entry-date-range-nights">{bookingNights > 0 ? `${bookingNights} night${bookingNights === 1 ? '' : 's'}` : 'Range pending'}</div>
                                        </div>
                                        <div className="feed-entry-date-range-picker">
                                            <DatePicker
                                                selected={selectedBookingStartDate}
                                                onChange={onFeedDateRangeChange}
                                                startDate={selectedBookingStartDate}
                                                endDate={selectedBookingEndDate}
                                                selectsRange
                                                inline
                                                monthsShown={2}
                                                showPopperArrow={false}
                                                calendarStartDay={0}
                                                minDate={null}
                                            />
                                        </div>
                                    </div>
                                    <label htmlFor="feedEntrySource">Source</label>
                                    <select
                                        id="feedEntrySource"
                                        name="source"
                                        value={feedEntryForm.source}
                                        onChange={onFeedEntryChange}
                                        required
                                    >
                                        <option value="">Select source</option>
                                        {BOOKING_SOURCES.map((source) => (
                                            <option key={source} value={source}>{source}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="feedEntryCleaningFee">Cleaning Fee (USD)</label>
                                    <input
                                        id="feedEntryCleaningFee"
                                        name="cleaningFee"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder={`Default: ${formatCurrency(property?.cleaningCost)}`}
                                        value={feedEntryForm.cleaningFee}
                                        onChange={onFeedEntryChange}
                                    />
                                    <span className="direct-booking-rules-hint" style={{ display: 'block', marginTop: -8, marginBottom: 8 }}>
                                        Leave blank to use the property's standard cleaning cost ({formatCurrency(property?.cleaningCost)}) for this booking.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <label htmlFor="feedEntryStartDate">Transaction Date</label>
                                    <input
                                        id="feedEntryStartDate"
                                        name="startDate"
                                        type="date"
                                        value={feedEntryForm.startDate}
                                        onChange={onFeedEntryChange}
                                        required
                                    />
                                    <label htmlFor="feedEntryCategory">Category</label>
                                    <select
                                        id="feedEntryCategory"
                                        name="category"
                                        value={feedEntryForm.category}
                                        onChange={onFeedEntryChange}
                                    >
                                        <option value="">Select category</option>
                                        <option value="Investment">Investment</option>
                                        <option value="Unexpected OpEx">Unexpected OpEx</option>
                                    </select>
                                </>
                            )}

                            <label htmlFor="feedEntryDescription">Description</label>
                            <input
                                id="feedEntryDescription"
                                name="description"
                                placeholder="Description"
                                value={feedEntryForm.description}
                                onChange={onFeedEntryChange}
                            />

                            <div className="modal-buttons modal-buttons-right">
                                <button type="submit" className="btn-primary">{feedEntryIsBooking ? (editingBookingId ? 'Save Booking' : 'Add Booking') : 'Add Transaction'}</button>
                                <button type="button" className="btn-secondary" onClick={closeFeedEntryModal}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showPricingRulesModal && (
                <div className="modal-overlay" onClick={() => setShowPricingRulesModal(false)}>
                    <div className="modal-content modal-content-wide property-modal-content property-modal-content-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Dynamic Pricing Rules</h3>
                            <button className="modal-close" onClick={() => setShowPricingRulesModal(false)}>&times;</button>
                        </div>
                        <div className="modal-form modal-form-pricing-rules">
                            <DirectBookingPricingRules propertyId={propertyId} compact />
                        </div>
                    </div>
                </div>
            )}

            {showPayoutModal && (
                <div className="modal-overlay" onClick={() => setShowPayoutModal(false)}>
                    <div className="modal-content modal-content-wide property-modal-content property-modal-content-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Payout Split Settings</h3>
                            <button className="modal-close" onClick={() => setShowPayoutModal(false)}>&times;</button>
                        </div>
                        <div className="modal-form">
                            {!canReadPayouts ? (
                                <p className="cohost-readonly-note">Your role can view this property but not payout settings.</p>
                            ) : payoutLoading ? (
                                <p>Loading payout settings...</p>
                            ) : (
                                <>
                                    <p className="cohost-readonly-note">Define payout beneficiaries and percentages. Splits must total 100% per effective start date.</p>
                                    {payoutConflicts.length > 0 && (
                                        <div className="cohost-readonly-note" style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', borderRadius: '10px', padding: '10px 12px' }}>
                                            <strong>Conflicts detected</strong>
                                            <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                                                {payoutConflicts.map((conflict, index) => (
                                                    <li key={`payout-conflict-${index}`}>{conflict.message || 'Potential overlap or total mismatch detected.'}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="payout-split-stack">
                                        {payoutSplitRows.map((row, index) => (
                                            <div className="payout-split-row" key={`payout-${index}`}>
                                                <select value={row.beneficiaryUserId} onChange={(e) => onPayoutSplitChange(index, 'beneficiaryUserId', e.target.value)} disabled={!canWritePayouts}>
                                                    <option value="">Select beneficiary</option>
                                                    {payoutBeneficiaries.map((entry) => (
                                                        <option key={entry.id} value={entry.id}>{entry.display || entry.email}</option>
                                                    ))}
                                                </select>
                                                <input type="number" step="0.001" min="0" max="100" placeholder="Split %" value={row.splitPercent} onChange={(e) => onPayoutSplitChange(index, 'splitPercent', e.target.value)} disabled={!canWritePayouts} />
                                                <input type="date" value={row.effectiveStartDate} onChange={(e) => onPayoutSplitChange(index, 'effectiveStartDate', e.target.value)} disabled={!canWritePayouts} />
                                                <input type="date" value={row.effectiveEndDate} onChange={(e) => onPayoutSplitChange(index, 'effectiveEndDate', e.target.value)} disabled={!canWritePayouts} />
                                                <input type="text" placeholder="Notes" value={row.notes} onChange={(e) => onPayoutSplitChange(index, 'notes', e.target.value)} disabled={!canWritePayouts} />
                                                {canWritePayouts && (
                                                    <button type="button" className="btn-secondary btn-inline btn-danger-inline" onClick={() => removePayoutSplitRow(index)}>
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                                        <h4 style={{ margin: '0 0 8px' }}>Recent Payout History</h4>
                                        {payoutHistory.length === 0 ? (
                                            <p className="cohost-readonly-note">No payout split change history yet.</p>
                                        ) : (
                                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px' }}>
                                                {payoutHistory.map((entry) => (
                                                    <div key={entry.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                        <div><strong>{entry.action || 'updated'}</strong> by {entry.changedBy?.display || entry.changedBy?.email || 'System'}</div>
                                                        <div className="cohost-readonly-note" style={{ marginTop: '2px' }}>{formatDate(entry.createdAt)} | Conflicts: {Number(entry.conflictCount) || 0}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="modal-buttons modal-buttons-right">
                                        {canWritePayouts && (
                                            <>
                                                <button type="button" className="btn-secondary" onClick={addPayoutSplitRow}>Add Split Row</button>
                                                <button type="button" className="btn-primary" onClick={savePayoutSplits} disabled={payoutLoading}>
                                                    {payoutLoading ? 'Saving...' : 'Save Split Settings'}
                                                </button>
                                            </>
                                        )}
                                        <button type="button" className="btn-secondary" onClick={() => setShowPayoutModal(false)}>Close</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showFeaturesModal && (
                <div className="modal-overlay" onClick={() => setShowFeaturesModal(false)}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Property Features</h3>
                            <button className="modal-close" onClick={() => setShowFeaturesModal(false)}>&times;</button>
                        </div>
                        <div className="modal-form">
                            {features.length === 0 ? (
                                <p className="no-features">No features added yet for this property.</p>
                            ) : (
                                <ul className="features-list">
                                    {features.map(f => (
                                        <li key={f.id} className="feature-item">
                                            <div className="item-content">
                                                <strong>{f.name}:</strong> {f.value}
                                            </div>
                                            <div className="item-actions">
                                                <button type="button" className="btn-secondary btn-inline" onClick={() => startFeatureEdit(f)}>
                                                    Edit
                                                </button>
                                                <button type="button" className="btn-secondary btn-inline btn-danger-inline" onClick={() => deleteFeature(f.id)}>
                                                    Delete
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <h4 className="inline-section-title">{editingFeatureId ? 'Edit Property Feature' : 'Add Property Feature'}</h4>
                            <form onSubmit={onSubmit} className="feature-form">
                                <input
                                    name="name"
                                    placeholder="Feature name (e.g., Bedrooms, Parking, Pool)"
                                    value={form.name}
                                    onChange={onChange}
                                    required
                                />
                                <input
                                    name="value"
                                    placeholder="Value (e.g., 3, Yes, Available)"
                                    value={form.value}
                                    onChange={onChange}
                                    required
                                />
                                <div className="form-action-row">
                                    <button type="submit" className="btn-primary">{editingFeatureId ? 'Save Feature' : 'Add Feature'}</button>
                                    {editingFeatureId && (
                                        <button type="button" className="btn-secondary" onClick={cancelFeatureEdit}>Cancel Edit</button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showNotesModal && (
                <div className="modal-overlay" onClick={() => setShowNotesModal(false)}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Property Notes</h3>
                            <button className="modal-close" onClick={() => setShowNotesModal(false)}>&times;</button>
                        </div>
                        <div className="modal-form">
                            <form onSubmit={onNoteSubmit} className="feature-form">
                                <input
                                    name="title"
                                    placeholder="Note title"
                                    value={noteForm.title}
                                    onChange={onNoteChange}
                                    required
                                />
                                <textarea
                                    name="note"
                                    placeholder="Write a note about this property..."
                                    value={noteForm.note}
                                    onChange={onNoteChange}
                                    rows="4"
                                    required
                                />
                                <div className="form-action-row">
                                    <button type="submit" className="btn-primary">{editingNoteId ? 'Save Note' : 'Add Note'}</button>
                                    {editingNoteId && (
                                        <button type="button" className="btn-secondary" onClick={cancelNoteEdit}>Cancel Edit</button>
                                    )}
                                </div>
                            </form>
                            {notes.length === 0 ? (
                                <p className="no-features">No notes yet for this property.</p>
                            ) : (
                                <ul className="details-list">
                                    {notes.map(n => (
                                        <li key={n.id} className="details-item">
                                            <div className="item-content">
                                                <strong>{n.title}</strong>: {n.note}
                                            </div>
                                            <div className="item-actions">
                                                <button type="button" className="btn-secondary btn-inline" onClick={() => startNoteEdit(n)}>
                                                    Edit
                                                </button>
                                                <button type="button" className="btn-secondary btn-inline btn-danger-inline" onClick={() => deleteNote(n.id)}>
                                                    Delete
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showMaintenanceModal && (
                <div className="modal-overlay" onClick={() => setShowMaintenanceModal(false)}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Maintenance Records</h3>
                            <button className="modal-close" onClick={() => setShowMaintenanceModal(false)}>&times;</button>
                        </div>
                        <div className="modal-form">
                            <form onSubmit={onMaintenanceSubmit} className="feature-form">
                                <input
                                    name="date"
                                    type="date"
                                    placeholder="Date"
                                    value={maintenanceForm.date}
                                    onChange={onMaintenanceChange}
                                    required
                                />
                                <input
                                    name="task"
                                    placeholder="Task Description"
                                    value={maintenanceForm.task}
                                    onChange={onMaintenanceChange}
                                    required
                                />
                                <input
                                    name="cost"
                                    type="number"
                                    step="0.01"
                                    placeholder="Cost"
                                    value={maintenanceForm.cost}
                                    onChange={onMaintenanceChange}
                                    required
                                />
                                <button type="submit" className="btn-primary">Add Maintenance</button>
                            </form>
                            {maintenanceRecords.length === 0 ? (
                                <p className="no-features">No maintenance records yet for this property.</p>
                            ) : (
                                <ul className="details-list">
                                    {maintenanceRecords.map(m => (
                                        <li key={m.id} className="details-item">
                                            <strong>{m.date}</strong> — {m.task} (Cost: {formatCurrency(m.cost)})
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showCostModal && (
                <div className="modal-overlay" onClick={() => setShowCostModal(false)}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Edit Standard Costs</h3>
                            <button className="modal-close" onClick={() => setShowCostModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={onCostSave} className="modal-form">
                            <label htmlFor="neighborhood">Neighborhood</label>
                            <input
                                id="neighborhood"
                                name="neighborhood"
                                type="text"
                                placeholder="e.g. Downtown, East Side"
                                value={costForm.neighborhood}
                                onChange={onCostChange}
                            />

                            <label htmlFor="latitude">Latitude</label>
                            <input
                                id="latitude"
                                name="latitude"
                                type="number"
                                step="any"
                                placeholder="e.g. 33.749000 (used for the public listings map)"
                                value={costForm.latitude}
                                onChange={onCostChange}
                            />

                            <label htmlFor="longitude">Longitude</label>
                            <input
                                id="longitude"
                                name="longitude"
                                type="number"
                                step="any"
                                placeholder="e.g. -84.388000 (used for the public listings map)"
                                value={costForm.longitude}
                                onChange={onCostChange}
                            />

                            <label htmlFor="rentCost">Monthly Rent</label>
                            <input
                                id="rentCost"
                                name="rentCost"
                                type="number"
                                step="0.01"
                                placeholder="e.g. 1800.00"
                                value={costForm.rentCost}
                                onChange={onCostChange}
                            />

                            <label htmlFor="acquisitionDate">Acquisition Date</label>
                            <input
                                id="acquisitionDate"
                                name="acquisitionDate"
                                type="date"
                                value={costForm.acquisitionDate}
                                onChange={onCostChange}
                            />

                            <label htmlFor="rentStartDate">Rent Start Date</label>
                            <input
                                id="rentStartDate"
                                name="rentStartDate"
                                type="date"
                                value={costForm.rentStartDate}
                                onChange={onCostChange}
                            />

                            <label htmlFor="wifiCost">Monthly WiFi Cost</label>
                            <input
                                id="wifiCost"
                                name="wifiCost"
                                type="number"
                                step="0.01"
                                placeholder="e.g. 60.00"
                                value={costForm.wifiCost}
                                onChange={onCostChange}
                            />

                            <label htmlFor="cleaningCost">Cleaning Cost per Booking</label>
                            <input
                                id="cleaningCost"
                                name="cleaningCost"
                                type="number"
                                step="0.01"
                                placeholder="e.g. 45.00"
                                value={costForm.cleaningCost}
                                onChange={onCostChange}
                            />

                            <label htmlFor="suppliesCost">Default Monthly Supplies Cost (Fallback)</label>
                            <input
                                id="suppliesCost"
                                name="suppliesCost"
                                type="number"
                                step="0.01"
                                placeholder="Used when no monthly supplies schedule is set"
                                value={costForm.suppliesCost}
                                onChange={onCostChange}
                            />

                            <label htmlFor="otherCost">Other Monthly Cost</label>
                            <input
                                id="otherCost"
                                name="otherCost"
                                type="number"
                                step="0.01"
                                placeholder="e.g. 15.00"
                                value={costForm.otherCost}
                                onChange={onCostChange}
                            />

                            <label htmlFor="pricingToolsCost">Pricing Tools Cost</label>
                            <input
                                id="pricingToolsCost"
                                name="pricingToolsCost"
                                type="number"
                                step="0.01"
                                placeholder="e.g. 20.00"
                                value={costForm.pricingToolsCost}
                                onChange={onCostChange}
                            />

                            <div className="modal-buttons modal-buttons-right">
                                <button type="submit" className="btn-primary">Save Standard Costs</button>
                                <button type="button" className="btn-secondary" onClick={() => setShowCostModal(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showInvestmentModal && (
                <div className="modal-overlay" onClick={closeInvestmentModal}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Manage Investments</h3>
                            <button className="modal-close" onClick={closeInvestmentModal}>&times;</button>
                        </div>
                        <div className="modal-form">
                            <form onSubmit={onInvestmentSubmit} className="feature-form">
                                <label htmlFor="investmentCost">Investment Cost</label>
                                <input
                                    id="investmentCost"
                                    name="cost"
                                    type="number"
                                    step="0.01"
                                    placeholder="e.g. 2500.00"
                                    value={investmentForm.cost}
                                    onChange={onInvestmentChange}
                                    required
                                />
                                <label htmlFor="investmentInvestedBy">Invested By</label>
                                <input
                                    id="investmentInvestedBy"
                                    name="investedBy"
                                    type="text"
                                    placeholder="e.g. David, Partner Name"
                                    value={investmentForm.investedBy}
                                    onChange={onInvestmentChange}
                                />
                                <label htmlFor="investmentReason">Reason</label>
                                <textarea
                                    id="investmentReason"
                                    name="reason"
                                    placeholder="What was this investment for?"
                                    value={investmentForm.reason}
                                    onChange={onInvestmentChange}
                                    rows="4"
                                    required
                                />
                                <div className="form-action-row">
                                    <button type="submit" className="btn-primary">{editingInvestmentId ? 'Save Investment' : 'Add Investment'}</button>
                                    {editingInvestmentId && (
                                        <button type="button" className="btn-secondary" onClick={cancelInvestmentEdit}>Cancel Edit</button>
                                    )}
                                </div>
                            </form>

                            {investmentSuccessMessage && (
                                <div className="inline-feedback inline-feedback-success">{investmentSuccessMessage}</div>
                            )}

                            {investments.length === 0 ? (
                                <p className="no-features">No investments recorded yet for this property.</p>
                            ) : (
                                <ul className="details-list investment-list">
                                    {investments.map((investment) => (
                                        <li key={investment.id} className="details-item investment-item">
                                            <div className="investment-item-topline">
                                                <div className="item-content">
                                                    <strong>{formatCurrency(investment.cost)}</strong>
                                                    <span>{formatDate(investment.createdAt)}{investment.investedBy ? ` · ${investment.investedBy}` : ''}</span>
                                                </div>
                                                <div className="item-actions">
                                                    <button type="button" className="btn-secondary btn-inline" onClick={() => startInvestmentEdit(investment)}>
                                                        Edit
                                                    </button>
                                                    <button type="button" className="btn-secondary btn-inline btn-danger-inline" onClick={() => deleteInvestment(investment.id)}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                            <div>{investment.reason}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SiteFeaturesPage;
